import { apiCall, API_CONFIG } from '../config/api';
import { getAuth } from 'firebase/auth';

// Script Generation API
export const generateScript = async (scriptData, authToken = null) => {
  try {
    const headers = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const response = await apiCall(API_CONFIG.ENDPOINTS.GENERATE_SCRIPT, {
      method: 'POST',
      headers,
      body: JSON.stringify(scriptData),
    });
    
    // Validate response format
    if (!response || !response.result) {
      throw new Error('Invalid response format from script generation API');
    }
    
    return response;
  } catch (error) {
    console.error('Script generation API error:', error);
    throw new Error(`Script generation failed: ${error.message}`);
  }
};

// Script Section Regeneration API
export const regenerateScriptSection = async (sectionData) => {
  try {
    const response = await apiCall(API_CONFIG.ENDPOINTS.REGENERATE_SCRIPT_SECTION, {
      method: 'POST',
      body: JSON.stringify(sectionData),
    });
    
    if (!response || !response.result) {
      throw new Error('Invalid response format from section regeneration API');
    }
    
    return response;
  } catch (error) {
    console.error('Section regeneration API error:', error);
    throw new Error(`Section regeneration failed: ${error.message}`);
  }
};

// Brief Generation API
export const generateBrief = async (briefData) => {
  return apiCall(API_CONFIG.ENDPOINTS.GENERATE_BRIEF, {
    method: 'POST',
    body: JSON.stringify(briefData),
  });
};

// Waitlist Subscription API
export const subscribeToWaitlist = async (emailData) => {
  return apiCall(API_CONFIG.ENDPOINTS.SUBSCRIBE_WAITLIST, {
    method: 'POST',
    body: JSON.stringify(emailData),
  });
};

// Creator Application API (JSON)
export const submitCreatorApplication = async (applicationData) => {
  return apiCall(API_CONFIG.ENDPOINTS.CREATOR_APPLICATION, {
    method: 'POST',
    body: JSON.stringify(applicationData),
  });
};

// Creator Application API (File Upload)
export const submitCreatorApplicationWithFiles = async (formData) => {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CREATOR_APPLICATION}`;
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      body: formData, // Don't set Content-Type header for FormData
    });
    
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  }
};

// Delete Creator Application API
export const deleteCreatorApplication = async (applicationData) => {
  return apiCall(API_CONFIG.ENDPOINTS.DELETE_CREATOR_APPLICATION, {
    method: 'POST',
    body: JSON.stringify(applicationData),
  });
};

// Approve Creator API
export const approveCreator = async (approvalData) => {
  return apiCall(API_CONFIG.ENDPOINTS.APPROVE_CREATOR, {
    method: 'POST',
    body: JSON.stringify(approvalData),
  });
};

// Approve Cancellation API
export const approveCancellation = async (cancellationData) => {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  // Get ID token for authentication
  const idToken = await user.getIdToken();
  
  console.log('🔵 approveCancellation: Calling API endpoint:', API_CONFIG.ENDPOINTS.APPROVE_CANCELLATION);
  console.log('🔵 approveCancellation: Payload:', cancellationData);
  console.log('🔵 approveCancellation: Has auth token:', !!idToken);
  
  try {
    const response = await apiCall(API_CONFIG.ENDPOINTS.APPROVE_CANCELLATION, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(cancellationData),
    });
    
    console.log('✅ approveCancellation: API response:', response);
    return response;
  } catch (error) {
    console.error('❌ approveCancellation: API call failed:', error);
    console.error('❌ approveCancellation: Error details:', {
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

// Hook Rating API
export const rateHook = async (hookData) => {
  try {
    const response = await apiCall(API_CONFIG.ENDPOINTS.RATE_HOOK, {
      method: 'POST',
      body: JSON.stringify(hookData),
    });
    
    if (!response || !response.result) {
      throw new Error('Invalid response format from hook rating API');
    }
    
    return response;
  } catch (error) {
    console.error('Hook rating API error:', error);
    throw new Error(`Hook rating failed: ${error.message}`);
  }
};

// Hook Generation API
export const generateHook = async (hookData) => {
  try {
    const response = await apiCall(API_CONFIG.ENDPOINTS.GENERATE_HOOK, {
      method: 'POST',
      body: JSON.stringify(hookData),
    });
    
    if (!response || !response.result) {
      throw new Error('Invalid response format from hook generation API');
    }
    
    return response;
  } catch (error) {
    console.error('Hook generation API error:', error);
    throw new Error(`Hook generation failed: ${error.message}`);
  }
};

// Content Enhancement API
export const enhanceContent = async (enhanceData) => {
  try {
    const response = await apiCall(API_CONFIG.ENDPOINTS.ENHANCE_CONTENT, {
      method: 'POST',
      body: JSON.stringify(enhanceData),
    });
    
    if (!response || !response.result) {
      throw new Error('Invalid response format from content enhancement API');
    }
    
    return response;
  } catch (error) {
    console.error('Content enhancement API error:', error);
    throw new Error(`Content enhancement failed: ${error.message}`);
  }
};

// Payment APIs

// Get available credit packages
export const getCreditPackages = async () => {
  try {
    const response = await apiCall(API_CONFIG.ENDPOINTS.GET_CREDIT_PACKAGES, {
      method: 'GET',
    });
    
    if (!response || !response.success || !response.packages) {
      throw new Error('Invalid response format from credit packages API');
    }
    
    return response.packages;
  } catch (error) {
    console.error('Get credit packages API error:', error);
    throw new Error(`Failed to get credit packages: ${error.message}`);
  }
};

// Test endpoint - Add credits directly (for testing only)
export const testAddCredits = async (userId, credits) => {
  try {
    const response = await apiCall(API_CONFIG.ENDPOINTS.TEST_ADD_CREDITS, {
      method: 'POST',
      body: JSON.stringify({
        userId,
        credits
      }),
    });
    
    if (!response || !response.success) {
      throw new Error('Invalid response format from test add credits API');
    }
    
    return response;
  } catch (error) {
    console.error('Test add credits API error:', error);
    throw new Error(`Failed to add credits: ${error.message}`);
  }
};

// Create Stripe checkout session
export const createCheckoutSession = async (packageId, userId, userEmail) => {
  try {
    // Get Firebase ID token for authentication
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    // Get Firebase ID token
    const idToken = await user.getIdToken();
    
    const baseUrl = window.location.origin;
    
    const response = await apiCall(API_CONFIG.ENDPOINTS.CREATE_CHECKOUT_SESSION, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        packageId,
        userId,
        userEmail,
        baseUrl
      }),
    });
    
    if (!response || !response.success || !response.url) {
      throw new Error('Invalid response format from checkout session API');
    }
    
    return response;
  } catch (error) {
    console.error('Create checkout session API error:', error);
    throw new Error(`Failed to create checkout session: ${error.message}`);
  }
}; 